import React, { Fragment, useEffect, useState } from "react";
import { IBulkUploadAppointments, IDoctor, IFile, ITextInput } from "../../helpers/Interfaces";
import ApiService from "../../services/ApiService";
import Modal from "../Modal/Modal";
import Papa from 'papaparse';
import moment from "moment";
import SelectFile from "./SelectFile";
import Upload from "./Upload";

import "./BulkUploadAppointments.scss";

type BulkUploadAppointmentsProps = {
  readonly open: boolean;
  readonly closeModal: (modal: "add" | "edit" | "view" | "bulk", shouldUpdate: boolean) => void;
  openModal: (modal: "add" | "edit" | "view") => void;
  readonly selectedDoctor: IDoctor | null;
}

function BulkUploadAppointments(props: BulkUploadAppointmentsProps) {

  const apiService = new ApiService();

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(0);
  const [clinics, setClinics] = useState([] as ITextInput[]);
  const [media, setMedia] = useState([] as IFile[]);
  const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });
  const [appointments, setAppointments] = useState([] as IBulkUploadAppointments[]);
  const [count, setCount] = useState({ total: 0, valid: 0, invalid: 0 });

  useEffect(() => {
    getClinics();
  }, [])

  function getClinics() {

    setLoading(true);

    let data = {
      doctorId: null
    }

    apiService.getClinicsByDoctorId(data).then((result: any) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      let clinicsArr = [] as ITextInput[];

      data.clinics.forEach((clinic: { clinicName: string; id: string; }) => {
        let data = {
          label: clinic.clinicName,
          value: clinic.id
        }

        clinicsArr.push(data);
      });

      setClinics(clinicsArr);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function clearData() {
    setLoading(false);
    setView(0);
    setClinics([]);
    setMedia([]);
    setShowAlert({ isError: false, message: "", open: false });
    setAppointments([]);
  }

  const handleClose = (shouldUpdate: boolean) => {
    clearData();
    props.closeModal("bulk", shouldUpdate);
  }

  const closeAlert = () => {
    let newShowAlert = showAlert;
    newShowAlert.open = false;
    setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
  }

  function saveAppointments() {

    setLoading(true);

    if (!appointments[0].clinicId) {
      setShowAlert({ isError: true, message: "Please Select a Clinic", open: true });
      setLoading(false);
      return;
    }

    const data = {
      appointments: appointments.map((app) => ({
        clinicId: app.clinicId,
        patientName: app.patientName,
        appointmentStart: moment(app.startTime).format("YYYY-MM-DDTHH:mm:ss"),
        appointmentEnd: moment(app.endTime).format("YYYY-MM-DDTHH:mm:ss"),
        doctorId: props.selectedDoctor?.doctorId ? props.selectedDoctor?.doctorId : null,
        notes: app.notes,
        paperworkReceived: getPaperworkReceived(app?.paperworkReceived),
        consultationMode: app?.consultationMode ? app?.consultationMode : null,
        invoiceStatus: app?.invoiceStatus ? app?.invoiceStatus : 0,
        appointmentStatus: getAppointmentStatus(app?.appointmentStatus)
      })),
    }

    apiService.bulkUploadAppointments(data).then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      let newShowAlert = showAlert;
      newShowAlert.isError = false;
      newShowAlert.message = "Appointments Added";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));

      handleClose(true);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function getPaperworkReceived(paperworkReceived: string | null | undefined) {
    if (paperworkReceived === null || paperworkReceived === undefined) {
      return false;
    }

    if (paperworkReceived.trim().toLowerCase() === "true") {
      return true;
    }

    return false;
  }

  function getAppointmentStatus(appointmentStatus: string | null | undefined) {
    if (appointmentStatus === null || appointmentStatus === undefined) {
      return 0;
    }

    if (appointmentStatus.trim().toLowerCase() === "true") {
      return 1;
    }

    return 0;
  }

  const handleNext = () => {
    setView(view + 1);
  }

  const handlePrevious = () => {
    setView(view - 1);
  }

  function getLocalDateObject(dateStr: string, timeStr: string): Date {
    return moment(`${dateStr} ${timeStr}`, "D/M/YYYY H:mm").toDate();
  }

  function validateData(patientName: string, dateOfAssessment: string, startTime: string, endTime: string) {
    if (!patientName || patientName.trim() === "") return false;

    const dateParts = dateOfAssessment.split('/');
    if (dateParts.length !== 3) return false;

    const [d, m, y] = dateParts.map(Number);
    const testDate = new Date(y, m - 1, d);
    if (isNaN(testDate.getTime())) return false;

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):(00|30)$/;

    if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
      return false;
    }

    return true;
  }

  function handleAppointmentSet(data: any[]) {

    const totalRows = data.length;

    const validRows = data.filter(row =>
      validateData(row?.patientName, row?.dateOfAssessment, row?.startTime, row?.endTime)
    );

    const skippedCount = totalRows - validRows.length;

    setCount({ total: totalRows, valid: validRows.length, invalid: skippedCount });

    const newAppointments: IBulkUploadAppointments[] = validRows.map((element) => {

      const dateObj = moment(element?.dateOfAssessment, "D/M/YYYY");

      return {
        patientName: element?.patientName,
        dateOfAssessment: dateObj.toDate(),
        startTime: getLocalDateObject(element?.dateOfAssessment, element?.startTime),
        endTime: getLocalDateObject(element?.dateOfAssessment, element?.endTime),
        notes: element?.notes,
        paperworkReceived: element?.paperworkReceived,
        appointmentStatus: element?.appointmentConfirmed,
        consultationMode: element?.consultationMode,
        invoiceStatus: element?.invoiceStatus
      };
    });

    if (newAppointments.length === 0) {
      setShowAlert({ isError: true, message: "No Valid Appointments Found in File", open: true });
      return;
    }

    setAppointments(newAppointments);

    setShowAlert({ isError: false, message: `Appointments Loaded`, open: true });
  }

  function handleFileUpload(file: IFile) {
    setMedia([file]);

    Papa.parse(file.fileUrl, {
      header: true,
      download: true,
      skipEmptyLines: true,
      complete: (results) => {
        handleAppointmentSet(results.data);
      },
      error: (_) => {
        let newShowAlert = { ...showAlert };
        newShowAlert.isError = true;
        newShowAlert.message = "Error Reading File";
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
      }
    });
  }

  function handleSelectedClinic(clinicId: string) {
    let appointmentsCopy = [...appointments]

    appointmentsCopy.forEach((appointment) => {
      appointment.clinicId = clinicId;
    })

    setAppointments(appointmentsCopy);
  }

  function nextToUpload() {
    if (media.length === 0) {
      let newShowAlert = { ...showAlert };
      newShowAlert.isError = true;
      newShowAlert.message = "Please Upload a File";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
      return;
    }

    handleNext();
  }

  return (
    <Modal showAlert={showAlert} closeAlert={closeAlert} open={props.open}
      close={() => handleClose(false)} style={"bulk-upload-appointments-card"}>
      <div className="bulk-upload-appointments-card-container">
        <span className="bulk-upload-appointments-card-title">Bulk Upload Appointments</span>
        {view === 0
          ? (<SelectFile loading={loading} setLoading={setLoading} media={media} setMedia={handleFileUpload}
            setShowAlert={setShowAlert} next={nextToUpload} />)
          : (<Fragment />)
        }
        {view === 1
          ? (<Upload loading={loading} setLoading={setLoading} back={handlePrevious} next={saveAppointments}
            appointments={appointments} setSelectedClinic={handleSelectedClinic} clinics={clinics} count={count} />)
          : (<Fragment />)
        }
      </div>
    </Modal>
  );
}

export default BulkUploadAppointments;
